/** Type a day's watch numbers in by hand (for days the phone didn't send, or to correct them). */
import { useState, type ReactElement } from 'react';
import { formatDayLong } from '../lib/dates';
import { errorMessage } from '../lib/logger';
import { manualActivityProblem } from '../lib/settings';
import type { ActivityDay, ManualActivityInput } from '../lib/types';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Segmented, Spinner } from './ui';

type Kind = 'active' | 'total';
const numOrNull = (s: string): number | null => { const t = s.trim(); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : Number.NaN; };

export function ManualActivityModal({ day, existing = null, onClose }: { day: string; existing?: ActivityDay | null; onClose: () => void }): ReactElement {
  const { source, toast, bump, today } = useApp();
  const [kind, setKind] = useState<Kind>(existing?.total_kcal !== null && existing?.total_kcal !== undefined && existing.active_kcal === null ? 'total' : 'active');
  const [kcal, setKcal] = useState<string>(existing ? String(existing.active_kcal ?? existing.total_kcal ?? '') : '');
  const [steps, setSteps] = useState<string>(existing?.steps !== null && existing?.steps !== undefined ? String(existing.steps) : '');
  const [resting, setResting] = useState<string>(existing?.resting_hr !== null && existing?.resting_hr !== undefined ? String(existing.resting_hr) : '');
  const [hrv, setHrv] = useState<string>(existing?.hrv_rmssd_ms !== null && existing?.hrv_rmssd_ms !== undefined ? String(existing.hrv_rmssd_ms) : '');
  const [sleepH, setSleepH] = useState<string>(existing?.sleep_minutes ? String(Math.floor(existing.sleep_minutes / 60)) : '');
  const [sleepM, setSleepM] = useState<string>(existing?.sleep_minutes ? String(existing.sleep_minutes % 60) : '');
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setError(null);
    const k: number | null = numOrNull(kcal);
    const h: number | null = numOrNull(sleepH);
    const m: number | null = numOrNull(sleepM);
    const values = [k, numOrNull(steps), numOrNull(resting), numOrNull(hrv), h, m];
    if (values.some((v) => v !== null && Number.isNaN(v))) { setError('Use plain numbers.'); return; }
    const sleep: number | null = h === null && m === null ? null : Math.round((h ?? 0) * 60 + (m ?? 0));
    const input: ManualActivityInput = {
      local_date: day,
      active_kcal: kind === 'active' ? k : null,
      total_kcal: kind === 'total' ? k : null,
      steps: numOrNull(steps) === null ? null : Math.round(numOrNull(steps) as number),
      resting_hr: numOrNull(resting),
      hrv_rmssd_ms: numOrNull(hrv),
      sleep_minutes: sleep,
      notes: notes.trim() === '' ? null : notes.trim().slice(0, 500),
    };
    const problem: string | null = manualActivityProblem(input);
    if (problem) { setError(problem); return; }
    setBusy(true);
    try {
      await source.saveManualActivity(input);
      toast('Watch numbers saved');
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      await source.deleteManualActivity(day);
      toast('Your typed numbers were removed');
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const num = (label: string, value: string, set: (v: string) => void, suffix: string, placeholder?: string): ReactElement => (
    <label className="field"><span>{label}</span>
      <div className="input-suffix"><input className="input" type="number" inputMode="decimal" min={0} value={value} placeholder={placeholder} onChange={(e) => set(e.target.value)} /><em>{suffix}</em></div>
    </label>
  );

  return (
    <Modal title={`Watch numbers for ${day === today ? 'today' : formatDayLong(day)}`} onClose={onClose} width={560} footer={
      <>
        {existing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => void remove()} disabled={busy}><Icon name="trash" size={15} /> Remove</button>}
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={18} />} Save</button>
      </>
    }>
      <div className="stack" style={{ gap: 14 }}>
        <div className="field"><span>Which calories does your watch show?</span>
          <Segmented<Kind> options={[{ value: 'active', label: 'Active calories (movement only)' }, { value: 'total', label: 'Total calories (includes resting)' }]} value={kind} onChange={setKind} />
          <small>{kind === 'active' ? 'Added to your resting burn (BMR) and digestion.' : 'Used as the burn itself, plus digestion. Your BMR setting is not added on top.'}</small>
        </div>
        {num(kind === 'active' ? 'Active calories' : 'Total calories', kcal, setKcal, 'kcal', kind === 'active' ? 'e.g. 850' : 'e.g. 3400')}
        <div className="form-grid">
          {num('Steps', steps, setSteps, 'steps')}
          {num('Resting heart rate', resting, setResting, 'bpm')}
          {num('HRV', hrv, setHrv, 'ms')}
          <div className="field"><span>Sleep</span>
            <div className="input-row">
              <div className="input-suffix"><input className="input" type="number" min={0} max={24} value={sleepH} onChange={(e) => setSleepH(e.target.value)} aria-label="Sleep hours" /><em>h</em></div>
              <div className="input-suffix"><input className="input" type="number" min={0} max={59} value={sleepM} onChange={(e) => setSleepM(e.target.value)} aria-label="Sleep minutes" /><em>min</em></div>
            </div>
          </div>
        </div>
        <label className="field"><span>Notes (optional)</span><input className="input" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. tired day, mostly desk work" /></label>
        <p className="faint" style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Typed numbers are used instead of anything the phone sent for this day. Remove them to go back to the phone’s numbers.</p>
        {error !== null && <div className="note err">{error}</div>}
      </div>
    </Modal>
  );
}
